import * as React from "react";
import { AlertCircle } from "lucide-react";

/**
 * Shared "a fetch failed" surface for the Organize section (and beyond).
 *
 * Why this exists: many organize surfaces used to silently swallow a failed fetch
 * and fall through to their EMPTY state, so "the backend is down" looked
 * identical to "you have nothing yet." This component makes the failure explicit and
 * always offers a retry. It mirrors the error card already used by OrganizeGoalsPage /
 * GoalHistoryPage so the look is consistent.
 *
 * Two shapes:
 *   - variant="block"  (default) — a full card, for a whole page/region that failed.
 *   - variant="inline" — a compact one-line row, for a lazy sub-section (e.g. a
 *     collapsible suggestions panel) where a full card would be too heavy.
 *
 * Offline is distinguished from a server error when it's cheap to know
 * (`navigator.onLine === false`), so the copy can be honest.
 */
export function LoadError({
  onRetry,
  message,
  variant = "block",
  retrying = false,
}: {
  onRetry: () => void;
  /** Optional override; otherwise a sensible online/offline default is used. */
  message?: string;
  variant?: "block" | "inline";
  /** Disables the retry button while a retry is in flight. */
  retrying?: boolean;
}) {
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  const text = message ?? (offline ? "You're offline — check your connection." : "Something went wrong. Your data is safe.");

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-clay-border bg-error-wash px-3 py-2.5">
        <AlertCircle className="w-4 h-4 text-error shrink-0" />
        <span className="flex-1 text-[0.8125rem] text-error-text">{text}</span>
        <button
          onClick={onRetry}
          disabled={retrying}
          className="text-[0.8125rem] font-medium text-primary hover:underline disabled:opacity-50"
        >
          {retrying ? "Retrying…" : "Try again"}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
      <AlertCircle className="w-10 h-10 text-error mx-auto mb-3" />
      <p className="text-error-text mb-1">{text}</p>
      <button
        onClick={onRetry}
        disabled={retrying}
        className="mt-3 px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:opacity-50"
      >
        {retrying ? "Retrying…" : "Try again"}
      </button>
    </div>
  );
}
